import { AnimatePresence, motion } from 'framer-motion'
import Highlight from '@/components/common/Highlight'
import AttachmentLinks from '@/components/common/AttachmentLinks'
import { useEffect, useMemo, useState } from 'react'
import { eventsApi, type ClassEventResponse } from '@/api/endpoints/events'
import { getHolidaysForYear, isInHolidayWeek } from '@/utils/holidays'
import { isDateOnlyEvent, formatEventWhen, toLocalDateString } from '@/utils/date'
import Spinner from '@/components/common/Spinner'

interface Props {
  open: boolean
  onClose: () => void
  classId: string
  className?: string | null
  searchQuery?: string
}

export default function ParentClassCalendarModal({ open, onClose, classId, className, searchQuery = '' }: Props) {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<ClassEventResponse[]>([])
  const [fcLoaded, setFcLoaded] = useState(false)
  const [FullCalendarComp, setFullCalendarComp] = useState<any>(null)
  const [fcPlugins, setFcPlugins] = useState<any[]>([])
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [holidays, setHolidays] = useState<Array<{ date: Date; name: string }>>([])

  // Load events
  useEffect(() => {
    let mounted = true
    const run = async () => {
      if (!open || !classId) return
      setLoading(true)
      try {
        const data = await eventsApi.list(classId)
        if (mounted) {
          setEvents(Array.isArray(data) ? data : [])
          
          // Load holidays for current year
          const year = new Date().getFullYear()
          const allHolidays = [
            ...getHolidaysForYear(year - 1),
            ...getHolidaysForYear(year),
            ...getHolidaysForYear(year + 1),
          ].map(h => ({ date: h.date, name: h.name }))
          setHolidays(allHolidays)
        }
      } catch {
        if (mounted) setEvents([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    run()
    return () => { mounted = false }
  }, [open, classId])

  // Load FullCalendar dynamically
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [{ default: FullCalendar }, dayGrid] = await Promise.all([
          import(/* @vite-ignore */'@fullcalendar/react'),
          import(/* @vite-ignore */'@fullcalendar/daygrid'),
        ])
        if (!cancelled) {
          setFullCalendarComp(() => FullCalendar)
          setFcPlugins([dayGrid.default])
          setFcLoaded(true)
        }
      } catch {
        if (!cancelled) setFcLoaded(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Prepare calendar events
  const calendarEvents = useMemo(() => {
    const classEvents = events.map(ev => ({
      id: ev.id,
      title: ev.topic || 'Untitled Event',
      start: isDateOnlyEvent(ev.event_date) ? (ev.event_date || '').slice(0, 10) : (ev.starts_at || ev.event_date),
      ...(isDateOnlyEvent(ev.event_date) ? { allDay: true } : {}),
      color: '#3b82f6', // Blue for class events
      extendedProps: { type: 'event', notes: ev.notes },
    }))

    const holidayEvents = holidays.map(holiday => ({
      id: `holiday-${holiday.date.toISOString()}`,
      title: holiday.name,
      start: toLocalDateString(holiday.date),
      color: '#f59e0b', // Amber for holidays
      extendedProps: { type: 'holiday' },
      display: 'background',
    }))

    return [...classEvents, ...holidayEvents]
  }, [events, holidays])

  // Filter events for search
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events
    const query = searchQuery.toLowerCase()
    return events.filter(ev => {
      const topic = (ev.topic || '').toLowerCase()
      const notes = (ev.notes || '').toLowerCase()
      return topic.includes(query) || notes.includes(query)
    })
  }, [events, searchQuery])

  // Sort events by date
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = a.starts_at || a.event_date || ''
      const dateB = b.starts_at || b.event_date || ''
      return dateA.localeCompare(dateB)
    })
  }, [filteredEvents])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose()
          }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-4 sm:p-6 hover-raise mx-2 sm:mx-4 max-h-[95vh] flex flex-col"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{className || 'Class'} Calendar</h2>
                <p className="text-sm text-gray-500 mt-1">Read-only view of class events and schedule</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50">
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-all duration-200 focus-ring ${
                      viewMode === 'calendar'
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setViewMode('calendar')}
                    aria-label="Switch to calendar view"
                  >
                    📅 Calendar
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-all duration-200 focus-ring ${
                      viewMode === 'list'
                        ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setViewMode('list')}
                    aria-label="Switch to list view"
                  >
                    📋 List
                  </button>
                </div>
                <button 
                  className="btn-secondary text-sm focus-ring" 
                  onClick={onClose}
                  aria-label="Close calendar"
                >
                  ✕ Close
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {loading ? (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <Spinner label="Loading calendar…" />
                </div>
              ) : viewMode === 'calendar' && fcLoaded && FullCalendarComp ? (
                <div className="flex flex-col h-full space-y-4">
                  <div className="flex-1 min-h-[500px]">
                    <FullCalendarComp
                      plugins={fcPlugins}
                      initialView="dayGridMonth"
                      height="auto"
                      events={calendarEvents}
                      editable={false}
                      selectable={false}
                      headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,dayGridWeek',
                      }}
                      dayCellClassNames={(arg: any) => {
                        const date = arg.date
                        const holidayCheck = isInHolidayWeek(date, holidays)
                        return holidayCheck.isHoliday ? 'bg-yellow-50/50' : ''
                      }}
                      eventDisplay="block"
                      eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short',
                      }}
                      allDaySlot={false}
                      slotMinTime="06:00:00"
                      slotMaxTime="22:00:00"
                      expandRows={true}
                      dayHeaderFormat={{ weekday: 'short' }}
                    />
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap items-center justify-center gap-6 text-xs pt-3 pb-2 border-t border-gray-200 bg-gray-50 rounded-b-lg px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded shadow-sm"></div>
                      <span className="font-medium text-gray-700">Class Events</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-amber-500 rounded shadow-sm"></div>
                      <span className="font-medium text-gray-700">Holidays</span>
                    </div>
                    <div className="text-gray-500 text-xs">
                      💡 This is a read-only view. Contact your teacher for any questions.
                    </div>
                  </div>
                </div>
              ) : viewMode === 'list' ? (
                <div className="space-y-4 overflow-y-auto flex-1">
                  {searchQuery && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        {filteredEvents.length === events.length
                          ? `Showing all ${events.length} events`
                          : `Found ${filteredEvents.length} of ${events.length} events matching "${searchQuery}"`}
                      </p>
                    </div>
                  )}

                  {sortedEvents.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                      <div className="text-4xl mb-3">📅</div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {searchQuery ? 'No events match your search' : 'No events scheduled yet'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {searchQuery
                          ? 'Try a different search term'
                          : 'Check back later for upcoming class events'}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100 border-2 border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                      {sortedEvents.map((ev) => {
                        const eventDate = ev.starts_at || ev.event_date
                        const isPast = eventDate ? new Date(eventDate) < new Date() : false
                        return (
                          <div
                            key={ev.id}
                            className="p-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className={`w-2 h-2 rounded-full ${isPast ? 'bg-gray-400' : 'bg-blue-500'} flex-shrink-0`}></div>
                                  <div className={`font-semibold text-base ${isPast ? 'text-gray-500' : 'text-gray-900'}`}>
                                    {searchQuery ? (
                                      <span><Highlight text={ev.topic || 'Untitled Event'} query={searchQuery} /></span>
                                    ) : (
                                      ev.topic || 'Untitled Event'
                                    )}
                                  </div>
                                  {ev.is_recurring && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                      🔄 Recurring
                                    </span>
                                  )}
                                </div>
                                {ev.notes && (
                                  <div
                                    className="text-sm text-gray-600 mt-2 pl-4 line-clamp-3"><Highlight text={ev.notes} query={searchQuery} /></div>
                                )}
                                <div className="pl-4"><AttachmentLinks ids={ev.attachment_ids} /></div>
                                <div className="text-xs text-gray-500 mt-3 pl-4 flex items-center gap-2">
                                  <span>📆</span>
                                  <span>
                                    {formatEventWhen(ev.event_date)}
                                  </span>
                                  {isPast && <span className="text-gray-400">(Past)</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-sm text-gray-600">Loading calendar...</div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
